import { useState } from 'react';
import moment from 'moment';
import { customToast } from '@/components/toast/custom-react-toastify/v1';
import { useUserPageContextValueHook } from '@/contexts/UserPageContextProvider';
import { useSubscriptionActionMutation, useSubscriptionQuery } from '@/hooks/ReactQuery/SubscriptionReactQuery/SubscriptionReactQuery';
import { PRO_PRICE, ProBadge, REFUND_DAYS } from './proPlan';
import { requestCardRegistration } from './tossBilling';
import { LegalLinks } from '@/components/legal/LegalLinks';

const FEATURES = [
    { label: '하단 슈룩 배지', free: '표시', pro: '숨김' },
    { label: '폰트 꾸미기 (페이지·텍스트·사진 설명)', free: '기본 폰트', pro: '15종' },
    { label: '프리미엄 배경 (그라디언트·메시·패턴·움직이는 배경)', free: '—', pro: '26종' },
    { label: '유입 분석 기간', free: '7·30일', pro: '90일' },
    { label: '방문자 환경 (기기·앱·캠페인)', free: '—', pro: '보기' },
    { label: '캠페인(UTM) 분석 (소스·매체·캠페인별 방문·클릭)', free: '—', pro: '보기' },
    { label: '링크별 상세 분석 (채널·기기·날짜별 클릭)', free: '많이 눌린 링크 10개', pro: '전체 링크 + 상세' },
    { label: '새 제안 문자 알림', free: '매일 12시 종합', pro: '새 제안마다 즉시' },
];

const STATUS = {
    ACTIVE: { label: '이용 중', className: 'bg-emerald-50 text-emerald-600' },
    CANCELED: { label: '해지 예정', className: 'bg-amber-50 text-amber-600' },
    PAST_DUE: { label: '결제 실패', className: 'bg-red-50 text-red-600' },
    EXPIRED: { label: '종료', className: 'bg-gray-100 text-gray-500' },
};

const PAYMENT_STATUS = { DONE: '결제 완료', FAILED: '결제 실패', CANCELED: '환불' };

const won = (n) => `${Number(n || 0).toLocaleString('ko-KR')}원`;
const date = (ms) => (ms ? moment(ms).format('YYYY.MM.DD') : '-');

/**
 * 마이 페이지 "요금제" 탭: 이 페이지의 슈룩 Pro 구독 (페이지 단위)
 */
export function FdSubscription(props) {
    const userPage = useUserPageContextValueHook()?.userPage;
    const query = useSubscriptionQuery({ userPageId: userPage?.id });
    const actionMutation = useSubscriptionActionMutation();
    const [agree, setAgree] = useState(false);
    const [opening, setOpening] = useState(false);

    const sub = query.data?.subscription;
    const payments = query.data?.payments || [];
    const active = sub?.isPro;
    const canPay = query.data?.billing?.canPay === true;

    const openCardWindow = async (mode) => {
        if (!canPay) {
            customToast.error('결제 준비 중입니다. 잠시 후 다시 시도해 주세요.');
            return;
        }
        if (mode === 'start' && !agree) {
            customToast.error('자동결제·환불 안내에 동의해 주세요.');
            return;
        }
        setOpening(true);
        try {
            await requestCardRegistration({ clientKey: query.data.billing.clientKey, userPageId: userPage.id, mode, subscriptionId: sub?.id });
        } catch (error) {
            // 사용자가 결제창을 닫은 경우(USER_CANCEL)는 조용히
            if (error?.code !== 'USER_CANCEL') customToast.error(error?.message || '결제창을 열지 못했어요.');
        } finally {
            setOpening(false);
        }
    }

    const runAction = (action, confirmText) => {
        if (confirmText && !window.confirm(confirmText)) return;
        actionMutation.mutate({ id: sub.id, action });
    }

    if (query.isLoading) {
        return <div className="h-64 animate-pulse rounded-2xl bg-white ring-1 ring-black/[0.06]" aria-busy="true" />;
    }

    return (
        <div className="flex flex-col gap-5">
            {/* 현재 상태 */}
            {active ? (
                <section className="rounded-2xl bg-white p-5 ring-1 ring-black/[0.06] sm:p-6">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <h2 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                            <ProBadge className="text-[11px]" /> 슈룩 Pro
                            <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${STATUS[sub.status]?.className}`}>{STATUS[sub.status]?.label}</span>
                        </h2>
                        <span className="text-sm font-semibold text-gray-700">월 {won(sub.amount)}</span>
                    </div>
                    <p className="mt-1 text-[13px] text-gray-500">@{userPage?.domainName} 페이지에 적용 중이에요.</p>

                    <dl className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div className="rounded-xl bg-gray-50 p-3.5">
                            <dt className="text-xs text-gray-500">{sub.status === 'CANCELED' ? 'Pro 이용 종료일' : '다음 결제일'}</dt>
                            <dd className="mt-1 text-[15px] font-bold text-gray-900">{date(sub.status === 'CANCELED' ? sub.currentPeriodEnd : sub.nextBillingAt)}</dd>
                        </div>
                        <div className="rounded-xl bg-gray-50 p-3.5">
                            <dt className="text-xs text-gray-500">이번 이용 기간</dt>
                            <dd className="mt-1 text-[15px] font-bold text-gray-900">{date(sub.currentPeriodStart)} ~ {date(sub.currentPeriodEnd)}</dd>
                        </div>
                        <div className="rounded-xl bg-gray-50 p-3.5">
                            <dt className="text-xs text-gray-500">결제 카드</dt>
                            <dd className="mt-1 truncate text-[15px] font-bold text-gray-900">{sub.card?.company || '카드'} {sub.card?.number || ''}</dd>
                        </div>
                    </dl>

                    {sub.status === 'PAST_DUE' &&
                        <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-[13px] text-red-700">
                            결제에 실패했어요. 매일 다시 시도하고 3번 실패하면 Pro 가 꺼져요. 카드를 바꾸면 바로 다시 결제해요.
                        </p>
                    }

                    <div className="mt-5 flex flex-wrap gap-2">
                        <button type="button" disabled={opening} onClick={() => openCardWindow('card')} className="rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-200 disabled:opacity-50">
                            카드 변경
                        </button>
                        {sub.status === 'CANCELED'
                            ? <button type="button" onClick={() => runAction('resume')} className="rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-gray-700">해지 취소</button>
                            : <button type="button" onClick={() => runAction('cancel', `해지할까요? ${date(sub.currentPeriodEnd)}까지는 Pro 를 계속 쓸 수 있고, 다음 결제는 하지 않아요.`)} className="rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-200">해지하기</button>
                        }
                        {sub.refundableUntil &&
                            <button type="button" onClick={() => runAction('refund', `환불할까요? 마지막 결제 ${won(sub.amount)}을 전액 취소하고 Pro 가 바로 꺼져요.`)} className="rounded-xl px-4 py-2.5 text-sm font-semibold text-[var(--redColor1)] hover:bg-red-50">
                                환불 요청 ({date(sub.refundableUntil)}까지)
                            </button>
                        }
                    </div>
                </section>
            ) : (
                <section className="overflow-hidden rounded-2xl bg-white ring-1 ring-black/[0.06]">
                    <div className="bg-gradient-to-br from-[#fdf2f4] via-[#f5effd] to-[#eef6fd] p-5 sm:p-6">
                        <h2 className="flex items-center gap-2 text-lg font-bold text-gray-900"><ProBadge className="text-[11px]" /> 슈룩 Pro</h2>
                        <p className="mt-1 text-[13px] text-gray-600 break-keep">@{userPage?.domainName} 페이지를 더 깔끔하게, 유입을 더 깊이 볼 수 있어요.</p>
                        <p className="mt-3 text-2xl font-extrabold text-gray-900">월 {won(PRO_PRICE)}</p>
                        {sub?.status === 'EXPIRED' && <p className="mt-1 text-xs text-gray-500">이전 구독은 {date(sub.currentPeriodEnd)}에 끝났어요.</p>}
                    </div>
                    <div className="p-5 sm:p-6">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="text-xs text-gray-500">
                                    <th className="pb-2 font-semibold">기능</th>
                                    <th className="pb-2 font-semibold">무료</th>
                                    <th className="pb-2 font-semibold text-gray-900">Pro</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {FEATURES.map(f => (
                                    <tr key={f.label}>
                                        <td className="py-2.5 pr-2 text-gray-700">{f.label}</td>
                                        <td className="py-2.5 pr-2 text-gray-400">{f.free}</td>
                                        <td className="py-2.5 font-semibold text-gray-900">{f.pro}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className="mt-5 rounded-xl bg-gray-50 p-4 text-[12.5px] leading-relaxed text-gray-600">
                            <p className="font-bold text-gray-800">자동결제 안내</p>
                            <ul className="mt-1 list-disc pl-4">
                                <li>지금 {won(PRO_PRICE)}이 결제되고, 매월 같은 날 자동으로 결제돼요.</li>
                                <li>언제든 이 화면에서 해지할 수 있고, 해지해도 이번 이용 기간 끝까지 Pro 를 쓸 수 있어요.</li>
                                <li>결제 후 {REFUND_DAYS}일 안에 환불을 요청하면 전액 환불돼요.</li>
                                <li>구독은 이 페이지(@{userPage?.domainName})에만 적용돼요.</li>
                            </ul>
                            <p className="mt-2">자세한 결제·해지·환불 기준은 <LegalLinks />을 확인해 주세요.</p>
                            <label className="mt-3 flex cursor-pointer items-center gap-2.5">
                                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="h-5 w-5 accent-gray-900" />
                                <span className="text-[13.5px] font-semibold text-gray-900">위 내용을 확인했고 자동결제에 동의합니다</span>
                            </label>
                        </div>

                        <button
                            type="button"
                            disabled={opening || !canPay}
                            onClick={() => openCardWindow('start')}
                            className="mt-4 w-full rounded-xl bg-gray-900 py-3.5 text-[15px] font-bold text-white transition hover:bg-gray-700 disabled:bg-gray-300"
                        >
                            {canPay ? `카드 등록하고 Pro 시작하기 · 월 ${won(PRO_PRICE)}` : '결제 준비 중'}
                        </button>
                        {!canPay && <p className="mt-2 text-center text-xs text-gray-500 break-keep">결제는 곧 열려요. 그때까지 Pro 기능은 모두 무료로 쓸 수 있어요.</p>}
                    </div>
                </section>
            )}

            {/* 결제 내역 */}
            {payments.length > 0 &&
                <section className="rounded-2xl bg-white p-5 ring-1 ring-black/[0.06] sm:p-6">
                    <h3 className="text-base font-bold text-gray-900">결제 내역</h3>
                    <ul className="mt-3 divide-y divide-gray-100">
                        {payments.map(p => (
                            <li key={p.id} className="flex items-center justify-between py-2.5 text-sm">
                                <span className="text-gray-600">{moment(p.approvedAt || p.createdAt).format('YYYY.MM.DD HH:mm')}</span>
                                <span className="flex items-center gap-3">
                                    <span className={`text-xs font-semibold ${p.status === 'DONE' ? 'text-emerald-600' : p.status === 'CANCELED' ? 'text-gray-500' : 'text-red-500'}`}>{PAYMENT_STATUS[p.status]}</span>
                                    <span className={`font-semibold tabular-nums ${p.status === 'DONE' ? 'text-gray-900' : 'text-gray-400 line-through'}`}>{won(p.amount)}</span>
                                </span>
                            </li>
                        ))}
                    </ul>
                </section>
            }
        </div>
    );
}
